import React, { useState } from 'react';
import { Contact } from '../../../types/Contact';
import { User as UserIcon, Pencil, X, Check } from 'lucide-react';
import { updateContactCore } from '../../../lib/contactsCore';
import { supabase } from '../../../lib/supabase';
import { copyField } from '../utils/contactHelpers';

interface BasicInfoSectionProps {
    formData: Contact;
    setFormData: React.Dispatch<React.SetStateAction<Contact>>;
    onSave: () => void;
    showNotification?: (message: string, type: 'success' | 'error') => void;
}

export default function BasicInfoSection({
    formData,
    setFormData,
    onSave,
    showNotification
}: BasicInfoSectionProps) {
    const [isBasicEditing, setIsBasicEditing] = useState(false);

    const saveBasic = async () => {
        if (!formData.id) return;
        try {
            await updateContactCore(supabase as any, String(formData.id), {
                first_name: (formData.first_name || null) as any,
                last_name: (formData.last_name || null) as any,
                title: (formData.title || null) as any,
                company: (formData.company || null) as any,
                city: (formData.city || null) as any,
                state: (formData.state || null) as any,
                country: (formData.country || null) as any,
            });
            setIsBasicEditing(false);
            onSave();
        } catch (e) {
            showNotification?.('Failed to save basic info', 'error');
        }
    };

    return (
        <div className="bg-gray-900 rounded-xl">
            <div className="w-full flex items-center justify-between px-5 py-4 rounded-t-xl">
                <div className="flex items-center space-x-2">
                    <UserIcon className="w-5 h-5 text-gray-400" />
                    <h3 className="text-lg font-semibold text-white">Basic</h3>
                </div>
                <div className="flex items-center gap-2">
                    {isBasicEditing ? (
                        <>
                            <button onClick={() => setIsBasicEditing(false)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Cancel" aria-label="Cancel">
                                <X className="w-4 h-4" />
                            </button>
                            <button onClick={saveBasic} className="p-2 rounded-md border border-gray-700 bg-gray-800 text-white hover:bg-gray-700" title="Save" aria-label="Save">
                                <Check className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <button onClick={() => setIsBasicEditing(true)} className="p-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-700" title="Edit" aria-label="Edit">
                            <Pencil className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
            <div className="px-5 pb-5">
                <div className="bg-gray-800 rounded-lg p-4">
                    {!isBasicEditing ? (
                        <ul className="text-sm text-gray-300 space-y-2">
                            <li>
                                <span className="text-gray-400">Name: </span>{' '}
                                {(formData.first_name || formData.last_name) ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Name', `${(formData.first_name || '').trim()} ${(formData.last_name || '').trim()}`.trim(), showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {`${formData.first_name || ''} ${formData.last_name || ''}`.trim()}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                            <li>
                                <span className="text-gray-400">Title: </span>{' '}
                                {formData.title ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Title', formData.title, showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {formData.title}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                            <li>
                                <span className="text-gray-400">Company: </span>{' '}
                                {formData.company ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Company', formData.company, showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {formData.company}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                            <li>
                                <span className="text-gray-400">Location: </span>{' '}
                                {(formData.city || formData.state || formData.country) ? (
                                    <button
                                        type="button"
                                        onClick={() => copyField('Location', [formData.city, formData.state, formData.country].filter(Boolean).join(', '), showNotification)}
                                        title="Click to copy"
                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full border border-transparent hover:border-gray-700 hover:bg-gray-700/40 hover:text-white cursor-pointer transition-colors"
                                    >
                                        {[formData.city, formData.state, formData.country].filter(Boolean).join(', ')}
                                    </button>
                                ) : (
                                    <span className="text-gray-400">—</span>
                                )}
                            </li>
                        </ul>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">First Name</label>
                                <input value={formData.first_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Last Name</label>
                                <input value={formData.last_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Title</label>
                                <input value={formData.title || ''} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Company</label>
                                <input value={formData.company || ''} onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">City</label>
                                <input value={formData.city || ''} onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">State</label>
                                <input value={formData.state || ''} onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Country</label>
                                <input value={formData.country || ''} onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))} className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-md text-gray-200" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}